import React, { Dispatch, FC, SetStateAction, useEffect } from 'react';
import CreatableSelect from 'react-select/creatable';
import Select, { SingleValue, StylesConfig } from 'react-select';

import { CheckboxList, NumberSlider } from 'components/molecules';
import { ToolTip } from 'components/atoms';
import { POST_MANUAL } from 'queries';
import toastSuccess from 'utils/toastSuccess';
import toastError from 'utils/toastError';
import { customStyles } from './utils/selectCustomStyles';
import { creativityOptions, languages, OptionType, tones } from './utils/staticData';

import { MessageFormType } from 'queries/types/MessageFormType';
import { MessageResultType } from 'queries/types/MessageResultType';

import './ManualType.scss';

type ManualTypeProps = {
  submitForm: boolean;
  manualFormData: MessageFormType;
  setManualFormData: Dispatch<SetStateAction<MessageFormType>>;
  setFormValidated: Dispatch<SetStateAction<boolean>>;
  setSubmitForm: Dispatch<SetStateAction<boolean>>;
  setLoadingState: Dispatch<SetStateAction<boolean>>;
  setResultData: Dispatch<SetStateAction<MessageResultType | null>>;
};

const ManualType: FC<ManualTypeProps> = ({
  setFormValidated,
  manualFormData,
  setManualFormData,
  submitForm,
  setSubmitForm,
  setLoadingState,
  setResultData,
}) => {
  useEffect(() => {
    const { text, tones } = manualFormData;
    if (text && tones.length) setFormValidated(true);
    else setFormValidated(false);
  }, [manualFormData]);

  useEffect(() => {
    if (submitForm) {
      setSubmitForm(false);

      POST_MANUAL(manualFormData).then((result) => {
        setLoadingState(false);
        if (result.success) {
          setResultData(result.data && 'data' in result.data ? result.data.data : null);
          toastSuccess(`Paraphrasing completed successfully!`);
        } else {
          setFormValidated(true);
          toastError(`Error while paraphrasing process!`);
        }
      });
    }
  }, [submitForm]);

  const handleChangeLanguages = (newLanguages: string[]) =>
    setManualFormData({ ...manualFormData, output_languages: newLanguages });

  const handleChangeOutputsPerTone = (newNumber: number) =>
    setManualFormData({ ...manualFormData, outputs_per_tone: newNumber });

  const handleChangeWordLimit = (newNumber: number) =>
    setManualFormData({ ...manualFormData, word_limit: newNumber });

  const handleChangeTones = (newTones: string[]) =>
    setManualFormData({ ...manualFormData, tones: newTones });

  const handleChangeCreativityLevel = (opt: SingleValue<OptionType>) => {
    setManualFormData({
      ...manualFormData,
      creativity_level: opt?.value ?? 'Balanced',
    });
  };

  return (
    <div className="mf__manual-type">
      <div className={'mf__manual-type__subBox'}>
        <ToolTip
          title={'Separate using double line breaks for multi-message paraphrasing.'}
          offset={[0, -89]}
          tooltipStyles={{ display: manualFormData.text ? 'none' : 'block' }}
        >
          <div className={'mf__manual-type__subBox-item'}>
            <label>Text</label>
            <textarea
              onChange={({ target }) =>
                setManualFormData({ ...manualFormData, text: target.value })
              }
              placeholder={'Input to paraphrase...'}
              autoFocus={true}
              value={manualFormData.text}
            />
          </div>
        </ToolTip>
        <ToolTip
          title={
            'Background information (in English) such as news articles, blogs that will enhance the variations of outputs.'
          }
          tooltipStyles={{ display: manualFormData.context ? 'none' : 'block' }}
          offset={[0, -89]}
        >
          <div className={'mf__manual-type__subBox-item'}>
            <label>Context</label>
            <textarea
              placeholder={'Input to include custom context...'}
              onChange={({ target }) =>
                setManualFormData({ ...manualFormData, context: target.value })
              }
              value={manualFormData.context}
            />
          </div>
        </ToolTip>
        <ToolTip
          placement={'top'}
          title={'Words that must appear in each of the variations of outputs.'}
          offset={[0, -29]}
        >
          <div className={'mf__manual-type__subBox-item'}>
            <label style={{ top: '1px' }}>Keywords</label>
            <CreatableSelect
              isClearable
              isMulti
              styles={customStyles}
              options={[]}
              value={manualFormData.keywords.map((item) => ({ value: item, label: item }))}
              onChange={(newValue) =>
                setManualFormData({
                  ...manualFormData,
                  keywords: newValue.map((item) => item.value),
                })
              }
              placeholder={'Input keywords which must appear in the output...'}
              noOptionsMessage={() => 'Type keyword...'}
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            />
          </div>
        </ToolTip>
        <ToolTip
          title={
            'Words or themes that can be mentioned or talked about in each of the variations of outputs.'
          }
          offset={[0, -29]}
          placement={'top'}
          tooltipStyles={{ maxWidth: '150%' }}
        >
          <div className={'mf__manual-type__subBox-item'}>
            <label style={{ top: '1px' }}>Topics</label>
            <CreatableSelect
              isClearable
              isMulti
              styles={customStyles}
              className={'mf__manual-type__select'}
              options={[]}
              value={manualFormData.topics.map((item) => ({ value: item, label: item }))}
              onChange={(newValue) =>
                setManualFormData({ ...manualFormData, topics: newValue.map((item) => item.value) })
              }
              placeholder={'Input topics that can be used...'}
              noOptionsMessage={() => 'Type topic...'}
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
            />
          </div>
        </ToolTip>
      </div>
      <div className={'mf__manual-type__separator'} />
      <div className={'mf__manual-type__subBox'}>
        <ToolTip
          placement={'top'}
          title={'Languages to include in the output (English is always included).'}
          offset={[0, -35]}
          tooltipStyles={{ maxWidth: '150%' }}
        >
          <div>
            <CheckboxList
              titleText={'Output Languages:'}
              options={languages}
              selectedValues={manualFormData.output_languages}
              setSelectedValues={handleChangeLanguages}
              className={'mf__manual-type__lang'}
            />
          </div>
        </ToolTip>
        <ToolTip
          placement={'bottom'}
          title={'The number of outputs per tone included in the output.'}
          offset={[0, -40]}
          tooltipStyles={{ maxWidth: '150%' }}
        >
          <div>
            <NumberSlider
              title={'Number of alternatives per tone to generate:'}
              value={manualFormData.outputs_per_tone}
              setValue={handleChangeOutputsPerTone}
              min={1}
              max={30}
            />
          </div>
        </ToolTip>
        <ToolTip
          placement={'bottom'}
          title={
            'The maximum number of words that the output will attempt to adhere to. Lower values may result in less varied outputs.'
          }
          offset={[0, -40]}
          tooltipStyles={{ maxWidth: '150%' }}
        >
          <div>
            <NumberSlider
              title={'Max Recommended Words:'}
              value={manualFormData.word_limit}
              setValue={handleChangeWordLimit}
              min={1}
              max={50}
            />
          </div>
        </ToolTip>
        <ToolTip
          placement="top"
          title={'Controls how free or strict the paraphrasing should be.'}
          offset={[0, -67]}
        >
          <div className={'mf__manual-type__subBox-item'}>
            <label style={{ top: '1px' }}>Creativity level</label>
            <Select
              styles={customStyles as StylesConfig<OptionType, false>}
              className={'mf__manual-type__select'}
              options={creativityOptions}
              isMulti={false}
              isClearable={false}
              isSearchable={false}
              value={creativityOptions.find(
                (o) => o.value === (manualFormData.creativity_level ?? 'Balanced'),
              )}
              onChange={handleChangeCreativityLevel}
              placeholder={'Select creativity...'}
            />
          </div>
        </ToolTip>
        <ToolTip placement={'top'} title={'Tones to include in the output.'} offset={[0, -41]}>
          <div>
            <CheckboxList
              titleText={'Adjust tones:'}
              options={tones}
              selectedValues={manualFormData.tones}
              setSelectedValues={handleChangeTones}
              className={'mf__manual-type__tone'}
            />
          </div>
        </ToolTip>
      </div>
    </div>
  );
};

export default ManualType;
